import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Layers, Plus } from 'lucide-react';
import { adminApi, type LocationNode } from '../../lib/adminApi';

/** Property structure: Building → Floor → Room (SPEC.md §5.1). */
export function LocationManager() {
  const { t } = useTranslation();
  const [tree, setTree] = useState<LocationNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);

  const load = useCallback(() => {
    adminApi.locations
      .tree()
      .then(setTree)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  if (error)
    return (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    );
  if (!tree) return <p className="text-sm text-slate-400">{t('admin.loading')}</p>;

  return (
    <div className="space-y-4">
      <AddLocationForm parentId={parentId} onDone={load} onError={setError} />
      <BatchRoomForm parentId={parentId} onDone={load} onError={setError} />

      {tree.length === 0 ? (
        <p className="text-sm text-slate-500">{t('adminLocations.empty')}</p>
      ) : (
        <ul className="rounded-xl border border-slate-800">
          {tree.map((node) => (
            <LocationRow
              key={node.id}
              node={node}
              depth={0}
              selectedId={parentId}
              onSelect={setParentId}
              onRetire={(id) => {
                adminApi.locations
                  .retire(id)
                  .then(load)
                  .catch((c: Error) => setError(c.message));
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function LocationRow({
  node,
  depth,
  selectedId,
  onSelect,
  onRetire,
}: {
  node: LocationNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRetire: (id: string) => void;
}) {
  const { t } = useTranslation();
  const selected = selectedId === node.id;

  return (
    <>
      <li
        className={`flex items-center gap-2 border-b border-slate-800 px-3 py-2 last:border-b-0 ${
          selected ? 'bg-brookfields-green-dark/60' : ''
        }`}
        style={{ paddingLeft: `${12 + depth * 18}px` }}
      >
        <button
          type="button"
          // Selecting sets the parent for the add forms above — the usual next action
          // after looking at a floor is putting something inside it.
          onClick={() => onSelect(selected ? null : node.id)}
          className="flex min-h-[44px] flex-1 items-center gap-2 bg-transparent text-left text-sm"
        >
          {depth > 0 && (
            <ChevronRight className="size-3 shrink-0 text-slate-600" aria-hidden="true" />
          )}
          <span className={node.isActive ? 'text-slate-100' : 'text-slate-600 line-through'}>
            {node.name}
          </span>
          {node.floor && <span className="text-xs text-slate-500">· {node.floor}</span>}
          {node.qrCode && <span className="text-xs text-sky-400">· {node.qrCode}</span>}
        </button>

        {node.isActive && (
          <button
            type="button"
            onClick={() => onRetire(node.id)}
            className="min-h-[36px] shrink-0 rounded bg-transparent px-2 text-xs text-slate-500"
          >
            {t('adminLocations.retire')}
          </button>
        )}
      </li>

      {node.children.map((child) => (
        <LocationRow
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          onRetire={onRetire}
        />
      ))}
    </>
  );
}

function AddLocationForm({
  parentId,
  onDone,
  onError,
}: {
  parentId: string | null;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [qrCode, setQrCode] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;

    try {
      await adminApi.locations.create({
        name: name.trim(),
        parentId,
        qrCode: qrCode.trim() || null,
      });
      setName('');
      setQrCode('');
      onDone();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Failed');
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400">
        <Plus className="size-3.5" aria-hidden="true" />
        {parentId ? t('adminLocations.addInside') : t('adminLocations.addTop')}
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('adminLocations.namePlaceholder')}
          aria-label={t('adminLocations.namePlaceholder')}
          className="min-w-[140px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <input
          value={qrCode}
          onChange={(event) => setQrCode(event.target.value)}
          placeholder={t('adminLocations.qrPlaceholder')}
          aria-label={t('adminLocations.qrPlaceholder')}
          className="min-w-[120px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={!name.trim()}
          className="min-h-[44px] rounded bg-brookfields-green ring-1 ring-brookfields-gold/70 px-4 text-sm font-medium text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
        >
          {t('adminLocations.add')}
        </button>
      </div>
    </form>
  );
}

/** Twenty near-identical rooms on a floor, added in one go rather than twenty. */
function BatchRoomForm({
  parentId,
  onDone,
  onError,
}: {
  parentId: string | null;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [from, setFrom] = useState('101');
  const [to, setTo] = useState('120');
  const [result, setResult] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!parentId) return;

    try {
      const outcome = await adminApi.locations.batch({
        parentId,
        namePrefix: 'Room ',
        from: Number(from),
        to: Number(to),
        qrPrefix: 'room-',
      });
      setResult(t('adminLocations.batchResult', { ...outcome }));
      onDone();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Failed');
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400">
        <Layers className="size-3.5" aria-hidden="true" />
        {t('adminLocations.batchHeading')}
      </p>

      {!parentId ? (
        <p className="text-xs text-slate-500">{t('adminLocations.batchNeedsParent')}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            aria-label={t('adminLocations.from')}
            className="w-24 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
          />
          <span className="text-xs text-slate-500">→</span>
          <input
            type="number"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            aria-label={t('adminLocations.to')}
            className="w-24 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
          />
          <button
            type="submit"
            className="min-h-[44px] rounded bg-slate-200 px-4 text-sm font-medium text-slate-900"
          >
            {t('adminLocations.batchAdd')}
          </button>
        </div>
      )}

      {result && <p className="mt-2 text-xs text-emerald-400">{result}</p>}
    </form>
  );
}
